import { useTranslation } from '../lib/i18n';

const languages = [
  { code: 'en', flag: '🇺🇸', name: 'English' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'ja', flag: '🇯🇵', name: '日本語' },
  { code: 'ar', flag: '🇸🇦', name: 'العربية' },
  { code: 'fa', flag: '🇮🇷', name: 'فارسی' },
  { code: 'tr', flag: '🇹🇷', name: 'Türkçe' },
  { code: 'he', flag: '🇮🇱', name: 'עברית' },
  { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt' },
  { code: 'ko', flag: '🇰🇷', name: '한국어' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
] as const;

interface LanguageSwitcherProps {
  variant?: 'flags' | 'dropdown' | 'compact' | 'responsive';
  className?: string;
}

export function LanguageSwitcher({ variant = 'flags', className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation();
  const currentLang = languages.find(l => l.code === language) || languages[0];
  
  // Responsive: Flag only on mobile, full dropdown on desktop
  if (variant === 'responsive') {
    return (
      <>
        {/* Mobile: Flag only dropdown */}
        <select 
          value={language} 
          onChange={(e) => setLanguage(e.target.value as any)}
          className={`sm:hidden bg-white/20 border border-white/30 text-white text-base rounded-lg px-2 py-1.5 focus:outline-none cursor-pointer hover:bg-white/30 transition-colors appearance-none ${className}`}
          style={{ width: '42px' }}
        >
          {languages.map(lang => (
            <option key={lang.code} value={lang.code} className="text-gray-900">
              {lang.flag}
            </option>
          ))}
        </select>
        {/* Desktop: Full dropdown with name */}
        <select 
          value={language} 
          onChange={(e) => setLanguage(e.target.value as any)}
          className={`hidden sm:block bg-black/20 backdrop-blur-md border border-white/30 text-white text-sm rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer hover:bg-black/30 transition-colors font-medium ${className}`}
        >
          {languages.map(lang => (
            <option key={lang.code} value={lang.code} className="text-gray-900">
              {lang.flag} {lang.name}
            </option>
          ))}
        </select>
      </>
    );
  }
  
  if (variant === 'dropdown') {
    return (
      <select 
        value={language} 
        onChange={(e) => setLanguage(e.target.value as any)}
        className={`bg-white/20 backdrop-blur-md border border-white/30 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer hover:bg-white/30 transition-all font-medium shadow-lg ${className}`}
        style={{ minWidth: '140px' }}
      >
        {languages.map(lang => (
          <option key={lang.code} value={lang.code} className="text-gray-900 bg-white">
            {lang.flag} {lang.name}
          </option>
        ))}
      </select>
    );
  }
  
  if (variant === 'compact') {
    return (
      <>
        {/* Mobile: Dropdown with current flag */}
        <div className={`sm:hidden ${className}`}>
          <select 
            value={language} 
            onChange={(e) => setLanguage(e.target.value as any)}
            className="bg-black/20 backdrop-blur-md border border-white/30 text-white text-sm rounded-lg pl-2 pr-6 py-2 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer hover:bg-black/30 transition-colors font-medium appearance-none"
            style={{ backgroundImage: 'none' }}
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code} className="text-gray-900">
                {lang.flag} {lang.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Desktop: Flag buttons */}
        <div className={`hidden sm:flex items-center gap-0.5 sm:gap-1 bg-black/10 backdrop-blur-sm rounded-full px-2 py-1 ${className}`}>
          {languages.slice(0, 6).map(lang => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code as any)}
              className={`text-xl sm:text-2xl p-1 transition-all active:scale-95 ${
                language === lang.code 
                  ? 'scale-110 bg-white/20 rounded-full' 
                  : 'opacity-60 hover:opacity-100'
              }`}
              title={lang.name}
              aria-label={`Switch to ${lang.name}`}
            >
              {lang.flag}
            </button>
          ))}
        </div>
      </>
    );
  }
  
  // Default: Full flags view
  return (
    <div className={`flex flex-wrap items-center justify-center gap-2 ${className}`}>
      {languages.map(lang => (
        <button
          key={lang.code}
          onClick={() => setLanguage(lang.code as any)}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all ${
            language === lang.code 
              ? 'bg-white/20 ring-2 ring-white shadow-lg scale-105' 
              : 'hover:bg-white/10 opacity-80 hover:opacity-100'
          }`}
          title={lang.name}
        >
          <span className="text-xl">{lang.flag}</span>
          <span className="text-xs font-medium text-white hidden sm:inline">{lang.name}</span>
        </button>
      ))}
    </div>
  );
}

export default LanguageSwitcher;

